import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { AutoCompleteMenu, FileLink, UploadInput } from "@/atoms";
import { FormContainer } from "@/components";
import { clientXmlFileFormData, fileData, inputData, permissionsData } from "@/data";
import { useUploadFile } from "@/hooks";
import { useClientXmlFileSchema } from "@/schemas";
import { canDo, renderFormControllerHandler, setUploadInputPreviousValueHandler } from "@/utils";

const ClientXmlFileForm = ({ action, loading, onSubmitForm, values }) => {
    const [finalizedFieldsArray, setFinalizedFieldsArray] = useState([]);

    const { t } = useTranslation();

    const selections = useSelector((state) => state.selectionsReducer);

    const currentClient = useSelector((state) => state.clientsReducer)?.currentClient;

    const {
        assetXmlDownloadUri,
        enableNewXmlTemplate,
        isApple,
        metadataXmlDownloadUri,
        takedownDownloadUri,
        videoXmlDownloadUri,
        xmlDownloadUri,
    } = currentClient;

    const downloadUrls = {
        assetsXmlFile: assetXmlDownloadUri,
        metadataXmlFile: metadataXmlDownloadUri,
        takedownFile: takedownDownloadUri,
        videoXmlFile: videoXmlDownloadUri,
        xmlFile: xmlDownloadUri,
    };

    const isFacebook = currentClient?.name.toLowerCase().includes("facebook");

    const isSpotify = currentClient?.name.toLowerCase().includes("spotify");

    const hasVideoXml = isSpotify && !!enableNewXmlTemplate;

    const schema = useClientXmlFileSchema();

    const { id } = useParams();

    const { user } = useAuth0();

    const formMethods = useForm({
        defaultValues: values,
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const { filesData, filesLoading, setFilesData, uploadFile } = useUploadFile();

    const { mediaEdit: mediaEditPermission, mediaRead: mediaReadPermission } = permissionsData.permissions.keys;

    const allowedPermissions = [mediaReadPermission, mediaEditPermission];

    useEffect(
        () => {
            const filteredFields = (
                isApple || enableNewXmlTemplate || isFacebook
                    ? clientXmlFileFormData.filter((field) => !field.notTakeDownUI)
                    : clientXmlFileFormData.filter((field) => !field.isTakeDownUI)
            ).filter((field) => !field.isSpotifyOnly || hasVideoXml);

            setFinalizedFieldsArray(filteredFields);
        },
        [isApple, enableNewXmlTemplate, isFacebook, hasVideoXml, clientXmlFileFormData], // eslint-disable-line
    );

    const xmlFileUploadHandler = (file, fieldName) => {
        const currentFile = file;

        let renamedFile = null;

        const fileNames = {
            assetsXmlFile: "asset-update.xml",
            metadataXmlFile: "metadata-update.xml",
            takedownFile: "takedown.xml",
            videoXmlFile: "video-update.xml",
            xmlFile: "metadata.xml",
        };

        if (isApple || enableNewXmlTemplate || isFacebook) {
            const fileName = `${currentClient.name}/${fileNames[fieldName]}`;

            renamedFile = new File([currentFile], fileName, {
                lastModified: currentFile.lastModified,
                type: currentFile.type,
            });
        }

        uploadFile(renamedFile || currentFile, formMethods, fieldName, parseInt(id), { isXmlType: true });
    };

    const {
        control,
        formState: { errors },
        setError,
        setValue,
        watch,
    } = formMethods;

    const renderFormControllerChildrenHandler = (field, label, name, type, __, selectionKey) => {
        switch (type) {
            case inputData.types.title:
                return (
                    <Box>
                        <Typography color="textPrimary" component="h4" variant="h6">
                            {t(`labels.${label}`)}
                        </Typography>
                    </Box>
                );
            case inputData.types.file:
                return (
                    <Box
                        sx={{
                            mb: 1,
                        }}
                    >
                        <UploadInput
                            accept={fileData.acceptedExtensions.xml}
                            color={errors[name]?.message ? "error" : "primary"}
                            error={errors[name]?.message}
                            field={field}
                            hasError={!!errors[name]?.message}
                            label={t(`labels.${label}`)}
                            loading={filesLoading?.[name]}
                            disabled={!canDo(user, allowedPermissions)}
                            onChange={(e) => xmlFileUploadHandler(e.target.files[0], name)}
                        />
                        {filesData?.[name] && (
                            <FileLink
                                hasDownload={!!isApple || !!enableNewXmlTemplate || !!isFacebook}
                                link={downloadUrls[name]}
                                name={filesData?.[name]?.name}
                            />
                        )}
                    </Box>
                );
            default:
                return (
                    <AutoCompleteMenu
                        defaultValue={selections?.[selectionKey]?.find((option) => option?.value === values?.[name])}
                        errors={errors}
                        getRenderedOption={(option) => option?.label}
                        label={label}
                        name={name}
                        optionLabel={(option) => option?.label}
                        options={selections?.[selectionKey]}
                        onChange={(_, option) => field.onChange(option?.value)}
                    />
                );
        }
    };

    useEffect(() => {
        const newFilesData = {};

        if (values?.[fileData.names.xml]) newFilesData.xmlFile = { name: t("labels.xmlFileUploaded") };

        if (values?.[fileData.names.takedown]) newFilesData.takedownFile = { name: t("labels.takedownFileUploaded") };

        if (values?.[fileData.names.metadataXml])
            newFilesData.metadataXmlFile = { name: t("labels.metadataXmlFileUploaded") };

        if (values?.[fileData.names.assetsXml])
            newFilesData.assetsXmlFile = { name: t("labels.assetsXmlFileUploaded") };

        if (values?.[fileData.names.videoXml]) newFilesData.videoXmlFile = { name: t("labels.videoXmlFileUploaded") };

        setFilesData(newFilesData);
    }, [values]); // eslint-disable-line

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.xml);
        },
        [watch(fileData.names.xml)], // eslint-disable-line
    );

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.metadataXml);
        },
        [watch(fileData.names.metadataXml)], // eslint-disable-line
    );

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.assetsXml);
        },
        [watch(fileData.names.assetsXml)], // eslint-disable-line
    );

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.videoXml);
        },
        [watch(fileData.names.videoXml)], // eslint-disable-line
    );

    return (
        <FormContainer
            action={action}
            disabled={filesLoading?.xmlFile || filesLoading?.videoXmlFile}
            formMethods={formMethods}
            loading={loading}
            data={renderFormControllerHandler(control, finalizedFieldsArray, renderFormControllerChildrenHandler)}
            hasSubmitButton={canDo(user, [mediaEditPermission, mediaReadPermission])}
            onSubmitForm={(data) => onSubmitForm(data)}
        />
    );
};

export default ClientXmlFileForm;

ClientXmlFileForm.propTypes = {
    action: PropTypes.string,
    loading: PropTypes.bool,
    onSubmitForm: PropTypes.func,
    values: PropTypes.object,
};
